import React from 'react';
import { getScaledColor } from '../utils/positionUtils';
import './Dial.css';

interface DialProps {
  position: number; // Position value from 0-100 (calculated using exponential function)
}

const Dial: React.FC<DialProps> = ({ position }) => {
  // Use the provided position (0-100) directly
  const percentage = Math.max(0, Math.min(100, position));
  
  // Calculate angle for needle (0-180 degrees for semi-circle, starting from left)
  // Convert percentage to angle: 0% = 0°, 100% = 180°
  const angleDeg = (percentage / 100) * 180;
  const angleRad = (angleDeg * Math.PI) / 180;
  
  // Get scaled color from deep red (worst) to very green (best)
  const arcColor = getScaledColor(percentage);
  
  // Calculate end point of the arc
  // Center is at (100, 100), radius is 80
  // Arc starts at left (20, 100) which is angle 180° (π radians)
  // We need to calculate the point at angle (180 - angleDeg) degrees from center
  const startAngle = Math.PI; // 180 degrees (left side)
  const currentAngle = startAngle - angleRad; // Subtract because we go counter-clockwise from left
  
  const centerX = 100;
  const centerY = 100;
  const radius = 80;
  
  const endX = centerX + radius * Math.cos(currentAngle);
  const endY = centerY - radius * Math.sin(currentAngle);
  
  // Large arc flag: 1 if angle > 180 degrees, 0 otherwise
  const largeArcFlag = angleDeg > 180 ? 1 : 0;
  
  // Needle end point
  const needleLength = 70;
  const needleX = centerX + needleLength * Math.cos(currentAngle);
  const needleY = centerY - needleLength * Math.sin(currentAngle);
  
  return (
    <div className="dial-container">
      <svg className="dial-svg" viewBox="0 0 200 120">
        {/* Background arc (gray) */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="8"
          strokeLinecap="round"
        />
        
        {/* Colored arc based on value */}
        {percentage > 0 && (
          <path
            d={`M 20 100 A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`}
            fill="none"
            stroke={arcColor}
            strokeWidth="8"
            strokeLinecap="round"
          />
        )}
        
        {/* Center point */}
        <circle cx={centerX} cy={centerY} r="4" fill="#1F2937" />
        
        {/* Needle */}
        <line
          x1={centerX}
          y1={centerY}
          x2={needleX}
          y2={needleY}
          stroke="#1F2937"
          strokeWidth="2"
          strokeLinecap="round"
        />
        
        {/* Tick mark at top center */}
        <line
          x1="100"
          y1="20"
          x2="100"
          y2="25"
          stroke="#9CA3AF"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
};

export default Dial;
